import { useState, useEffect, useCallback, useMemo } from "react";
import {
    RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight, Facebook, PenLine,
    Globe, Activity, PauseCircle, Trophy, XCircle, HelpCircle, X, Eye, EyeOff,
    SlidersHorizontal,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { LEAD_STAGE_REPORT_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../hooks/usePersistedState";
import StateFilterSelect, { BLANK_STATE } from "../../components/common/StateFilterSelect";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import { formatDate, formatTime } from "../../utils/datetime";
import TruncatedText from "../../components/ui/truncated-text";
import dayjs from "dayjs";

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

// Backend expects DD-MM-YYYY
const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = d => d.format("DD-MM-YYYY");
    switch (preset) {
        case "today":      return { from_date: fmt(today), to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")), to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7, "day")), to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")), to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"), to_date: dayjs(customTo).format("DD-MM-YYYY") }
            : {};
        default:           return {};
    }
};

const ORIGIN_STYLE = {
    Meta:    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    Website: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
    Manual:  "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};

const ORIGIN_ICON = { Meta: Facebook, Website: Globe, Manual: PenLine };

const OriginPill = ({ origin, hint }) => {
    const Icon = ORIGIN_ICON[origin] || PenLine;
    return (
        <span title={hint}
            className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
                ORIGIN_STYLE[origin] || ORIGIN_STYLE.Manual)}>
            <Icon className="h-2.5 w-2.5" />
            {origin}
        </span>
    );
};

// Phase presentation only. Which stages belong to which phase is decided by the
// backend (customers/lead_stage_report.py PHASES) and arrives on every row, so
// there is exactly one definition of the funnel and this file cannot drift from it.
const PHASE_STYLE = {
    open:    { icon: Activity,    accent: "text-indigo-600 dark:text-indigo-400",
               bar: "bg-indigo-500",  chip: "bg-indigo-600 border-indigo-700",
               soft: "border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/60 dark:bg-indigo-900/10" },
    stalled: { icon: PauseCircle, accent: "text-amber-600 dark:text-amber-400",
               bar: "bg-amber-500",   chip: "bg-amber-600 border-amber-700",
               soft: "border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-900/10" },
    won:     { icon: Trophy,      accent: "text-emerald-600 dark:text-emerald-400",
               bar: "bg-emerald-500", chip: "bg-emerald-600 border-emerald-700",
               soft: "border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-900/10" },
    lost:    { icon: XCircle,     accent: "text-rose-600 dark:text-rose-400",
               bar: "bg-rose-500",    chip: "bg-rose-600 border-rose-700",
               soft: "border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-900/10" },
    other:   { icon: HelpCircle,  accent: "text-slate-500 dark:text-slate-400",
               bar: "bg-slate-400",   chip: "bg-slate-600 border-slate-700",
               soft: "border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30" },
};
const phaseStyle = (key) => PHASE_STYLE[key] || PHASE_STYLE.other;

const num = (n) => (n ?? 0).toLocaleString("en-IN");

/** One removable chip in the "Filtering by" strip. */
const FilterChip = ({ label, value, onClear }) => (
    <span className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[10px] font-medium
                     bg-indigo-50 text-indigo-700 border border-indigo-200
                     dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800/60">
        <span className="opacity-60">{label}</span>
        <span className="font-semibold">{value}</span>
        <button onClick={onClear} title={`Remove ${label} filter`}
            className="ml-0.5 rounded-full p-0.5 hover:bg-indigo-200/70 dark:hover:bg-indigo-800/50">
            <X className="h-2.5 w-2.5" />
        </button>
    </span>
);

export default function LeadStageReport() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [datePreset, setDatePreset] = usePersistedState("lead_stage_preset", "all");
    const [customFrom, setCustomFrom] = usePersistedState("lead_stage_from", "");
    const [customTo,   setCustomTo]   = usePersistedState("lead_stage_to", "");
    const [originFilter, setOriginFilter] = usePersistedState("lead_stage_origin", "");
    // A fresh key, not the old `lead_stage_stage`: that one holds a bare string
    // in any tab still open from the previous build, and reading it back as an
    // array would break on first render.
    const [stageFilter, setStageFilter] = usePersistedState("lead_stage_stages", []);
    const [selectedUserId, setSelectedUserId] = usePersistedState("lead_stage_user", "");
    const [stateFilter, setStateFilter] = usePersistedState("lead_stage_state", "");
    const [showEmpty, setShowEmpty] = usePersistedState("lead_stage_show_empty", false);

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [resp, setResp] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Defensive: a stale string from an older build would otherwise reach
    // .includes()/.join() as a string and filter by single characters.
    const stages = useMemo(
        () => (Array.isArray(stageFilter) ? stageFilter : stageFilter ? [stageFilter] : []),
        [stageFilter]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL)
            .then(r => { if (r?.status === "success") setUsers(r.data || []); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({
            ...resolveDateRange(datePreset, customFrom, customTo),
            page: String(page),
            page_size: String(pageSize),
        });
        if (originFilter) params.set("origin", originFilter);
        if (stages.length) params.set("lead_stage", stages.join(","));
        if (selectedUserId) params.set("assigned_user_id", selectedUserId);
        if (stateFilter) params.set("state", stateFilter);
        if (search) params.set("search", search);

        fetch(`${LEAD_STAGE_REPORT_URL}?${params.toString()}`)
            .then(r => {
                if (r?.status === "success") setResp(r);
                else ToastMsg("error", r?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datePreset, customFrom, customTo, originFilter, stages,
        selectedUserId, stateFilter, search, page, pageSize]);

    useEffect(() => { load(); }, [load]);

    // Any filter change invalidates the current page number — page 7 of the old
    // result set is not page 7 of the new one.
    useEffect(() => { setPage(1); },
        [datePreset, customFrom, customTo, originFilter, stages, selectedUserId, stateFilter, search]);

    const s = resp?.summary;
    // Memoised on `resp` rather than written as `resp?.by_stage || []`: that
    // fallback allocates a fresh array every render, which made the useMemos
    // below recompute on each one and their dependency lists meaningless.
    const byStage = useMemo(() => resp?.by_stage || [], [resp]);
    const byPhase = useMemo(() => resp?.by_phase || [], [resp]);
    const rows = useMemo(() => resp?.data || [], [resp]);
    const totalPages = resp?.total_pages || 1;
    const totalCount = resp?.total_count || 0;

    // Groups in funnel order, taken from the row order the API already sorted.
    // Labels come from by_phase where present, with a fallback so a phase the
    // rollup omitted (an all-zero "unplaced" bucket) still renders its rows
    // rather than dropping them silently.
    const groups = useMemo(() => {
        const meta = new Map(byPhase.map(p => [p.phase, p]));
        const seen = [];
        byStage.forEach(r => { if (!seen.includes(r.phase)) seen.push(r.phase); });
        return seen.map(key => {
            const members = byStage.filter(r => r.phase === key);
            return meta.get(key) || {
                phase: key, label: "Unplaced",
                description: "Stages the report has no phase for",
                stages: members.map(r => r.lead_stage),
                total: members.reduce((a, r) => a + r.total, 0),
                pct_of_total: 0,
                stages_in_use: members.filter(r => r.total).length,
            };
        });
    }, [byStage, byPhase]);

    const emptyCount = byStage.filter(r => !r.total).length;
    const hasFilters = datePreset !== "all" || originFilter || stages.length
        || selectedUserId || stateFilter || search;

    const toggleStage = (stage) => setStageFilter(
        stages.includes(stage) ? stages.filter(x => x !== stage) : [...stages, stage]);

    // Clicking a phase selects every stage in it, or clears them if the whole
    // phase is already selected — so the tile toggles rather than accumulating.
    const togglePhase = (group) => {
        const all = group.stages;
        const allOn = all.length > 0 && all.every(x => stages.includes(x));
        setStageFilter(allOn ? stages.filter(x => !all.includes(x))
                             : [...new Set([...stages, ...all])]);
    };

    const clearAll = () => {
        setDatePreset("all"); setCustomFrom(""); setCustomTo("");
        setOriginFilter(""); setStageFilter([]); setSelectedUserId(""); setStateFilter("");
        setSearchInput(""); setSearch("");
    };

    const repName = (u) =>
        [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
    const selectedRep = users.find(u => String(u.id) === String(selectedUserId));

    const selectClass = "appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer";

    return (
        <ContainerCard>
            <div className="flex-shrink-0">
                {/* ── header ── */}
                <div className="flex items-start justify-between mb-3 px-1 gap-3">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Stages</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Where every lead sits in the funnel, split by whether it came from a
                            Meta ad, a website form, or somebody's keyboard.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {s && (
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap"
                                title="Stages holding at least one lead, out of every stage defined">
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {s.stages_in_use}
                                </span>
                                {" of "}{s.stages_total ?? byStage.length} stages in use
                            </span>
                        )}
                        <Button onClick={load} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* ── filter bar ── */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 p-2.5 mb-3">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 self-center pr-1">
                            <SlidersHorizontal className="h-3 w-3" />
                            Filters
                        </div>

                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Created</span>
                            <div className="relative">
                                <select value={datePreset}
                                    onChange={e => {
                                        setDatePreset(e.target.value);
                                        if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                                    }}
                                    className={selectClass}>
                                    {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            </div>
                        </label>

                        {datePreset === "custom" && (
                            <label className="flex flex-col gap-1">
                                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Range</span>
                                <div className="flex items-center gap-1.5">
                                    <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                                        className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                                    <span className="text-xs text-gray-400">to</span>
                                    <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                                        className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                                </div>
                            </label>
                        )}

                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Assigned to</span>
                            <div className="relative">
                                <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                                    className={selectClass}>
                                    <option value="">All Reps</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{repName(u)}</option>)}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            </div>
                        </label>

                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">State</span>
                            <StateFilterSelect value={stateFilter} onChange={setStateFilter} />
                        </label>

                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Lead</span>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                <input value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") setSearch(searchInput.trim()); }}
                                    onBlur={() => setSearch(searchInput.trim())}
                                    placeholder="Name or phone…"
                                    className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 w-44" />
                            </div>
                        </label>

                        <label className="flex flex-col gap-1 min-w-0">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Origin</span>
                            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                                {[["", "All", s?.total, null],
                                  ["Meta", "Meta ads", s?.meta, s?.meta_pct],
                                  ["Website", "Website", s?.website, s?.website_pct],
                                  ["Manual", "Manual", s?.manual, s?.manual_pct]].map(([value, label, count, pct], i) => (
                                    <button key={value || "all"} onClick={() => setOriginFilter(value)}
                                        title={pct != null ? `${pct}% of all leads in range` : "Every origin"}
                                        className={cn("px-2.5 py-1.5 text-xs transition-colors whitespace-nowrap",
                                            i > 0 && "border-l border-gray-200 dark:border-gray-700",
                                            originFilter === value
                                                ? "bg-gray-700 text-white dark:bg-gray-200 dark:text-gray-900"
                                                : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700")}>
                                        {label}
                                        {count != null && <span className="ml-1.5 tabular-nums opacity-70">{num(count)}</span>}
                                    </button>
                                ))}
                            </div>
                        </label>
                    </div>

                    {/* ── what is actually applied, each removable ── */}
                    {hasFilters && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-700">
                            <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 pr-0.5">Filtering by</span>
                            {datePreset !== "all" && (
                                <FilterChip label="Created" onClear={() => { setDatePreset("all"); setCustomFrom(""); setCustomTo(""); }}
                                    value={DATE_PRESETS.find(p => p.value === datePreset)?.label || datePreset} />
                            )}
                            {selectedUserId && (
                                <FilterChip label="Rep" value={selectedRep ? repName(selectedRep) : selectedUserId}
                                    onClear={() => setSelectedUserId("")} />
                            )}
                            {stateFilter && (
                                <FilterChip label="State" value={stateFilter === BLANK_STATE ? "No state" : stateFilter}
                                    onClear={() => setStateFilter("")} />
                            )}
                            {originFilter && (
                                <FilterChip label="Origin" value={originFilter} onClear={() => setOriginFilter("")} />
                            )}
                            {search && (
                                <FilterChip label="Search" value={search}
                                    onClear={() => { setSearch(""); setSearchInput(""); }} />
                            )}
                            {stages.map(st => (
                                <FilterChip key={st} label="Stage" value={st} onClear={() => toggleStage(st)} />
                            ))}
                            <button onClick={clearAll}
                                className="ml-1 text-[10px] font-medium text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 underline decoration-dotted">
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* ── phase strip: click a phase to filter to all its stages ──
                     One slim row rather than four 110px cards. The page scrolls
                     normally (ContainerCard is a fixed h-[calc(100vh-64px)] with
                     overflow-y-auto), so every pixel spent above the tables is a
                     pixel of lead list pushed off a 768px laptop screen. */}
                <div className="flex flex-wrap items-stretch gap-1.5 mb-3">
                    {groups.map(g => {
                        const st = phaseStyle(g.phase);
                        const Icon = st.icon;
                        const allOn = g.stages.length > 0 && g.stages.every(x => stages.includes(x));
                        const someOn = !allOn && g.stages.some(x => stages.includes(x));
                        return (
                            <button key={g.phase} onClick={() => togglePhase(g)}
                                title={`${g.description}\n${g.stages_in_use} of ${g.stages.length} stages in use\n\nClick to ${allOn ? "clear" : "filter to"} all ${g.stages.length} of these stages`}
                                className={cn("flex-1 min-w-[150px] rounded-lg border px-2.5 py-1.5 text-left transition-all",
                                    allOn
                                        ? "border-transparent ring-2 ring-indigo-500 " + st.soft
                                        : someOn
                                            ? "ring-1 ring-indigo-300 dark:ring-indigo-700 " + st.soft
                                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50")}>
                                <div className="flex items-center gap-1.5">
                                    <Icon className={cn("h-3.5 w-3.5 flex-shrink-0", st.accent)} />
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        {g.label}
                                    </span>
                                    <span className="ml-auto text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                                        {num(g.total)}
                                    </span>
                                    <span className="text-[10px] text-gray-400 tabular-nums w-9 text-right">
                                        {g.pct_of_total}%
                                    </span>
                                </div>
                                <div className="mt-1 h-0.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div className={cn("h-full", st.bar)} style={{ width: `${g.pct_of_total}%` }} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── stage breakdown: the original cross-tab, grouped by phase ── */}
            <div className="mb-4 flex-shrink-0">
                <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Stage breakdown
                        <span className="ml-1.5 font-normal text-[10px] text-gray-400">
                            click any stage to filter
                        </span>
                    </h2>
                    {emptyCount > 0 && (
                        <button onClick={() => setShowEmpty(!showEmpty)}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                            {showEmpty ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            {showEmpty ? "Hide" : "Show"} {emptyCount} empty stage{emptyCount === 1 ? "" : "s"}
                        </button>
                    )}
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-800/60">
                            <tr className="text-gray-500 dark:text-gray-400">
                                <th className="px-3 py-2 text-left font-medium">Stage</th>
                                <th className="px-3 py-2 text-right font-medium">From Meta</th>
                                <th className="px-3 py-2 text-right font-medium">Website</th>
                                <th className="px-3 py-2 text-right font-medium">Manual</th>
                                <th className="px-3 py-2 text-right font-medium">Total</th>
                                <th className="px-3 py-2 text-left font-medium w-40">Share of all leads</th>
                            </tr>
                        </thead>
                        {groups.map(g => {
                            const st = phaseStyle(g.phase);
                            const Icon = st.icon;
                            const members = byStage.filter(
                                r => r.phase === g.phase && (showEmpty || r.total));
                            if (!members.length) return null;
                            return (
                                <tbody key={g.phase} className="divide-y divide-gray-100 dark:divide-gray-700">
                                    <tr className={cn("border-t border-gray-200 dark:border-gray-700", st.soft)}>
                                        <td colSpan={4} className="px-3 py-1.5">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Icon className={cn("h-3 w-3", st.accent)} />
                                                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                                                    {g.label}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-normal normal-case">
                                                    {g.description}
                                                </span>
                                            </span>
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-gray-700 dark:text-gray-200">
                                            {num(g.total)}
                                        </td>
                                        <td className="px-3 py-1.5 text-[10px] text-gray-400 tabular-nums">
                                            {g.pct_of_total}% of all
                                        </td>
                                    </tr>
                                    {members.map(r => {
                                        const on = stages.includes(r.lead_stage);
                                        return (
                                            <tr key={r.lead_stage}
                                                onClick={() => toggleStage(r.lead_stage)}
                                                title={on ? "Click to remove this stage from the filter"
                                                          : "Click to filter to this stage"}
                                                className={cn("cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50",
                                                    !r.total && "opacity-45",
                                                    on && "bg-indigo-50 dark:bg-indigo-900/20")}>
                                                <td className="px-3 py-2 pl-6">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <span className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0",
                                                            on ? "bg-indigo-500"
                                                               : "border border-gray-300 dark:border-gray-600")} />
                                                        <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium",
                                                            getStageBadgeClass(r.lead_stage))}>
                                                            {r.lead_stage}
                                                        </span>
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.meta)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.website)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{num(r.manual)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900 dark:text-white">{num(r.total)}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden flex">
                                                            <div className="h-full bg-blue-500"
                                                                style={{ width: `${r.total ? (r.meta / r.total) * r.pct_of_total : 0}%` }} />
                                                            <div className="h-full bg-teal-500"
                                                                style={{ width: `${r.total ? (r.website / r.total) * r.pct_of_total : 0}%` }} />
                                                            <div className="h-full bg-slate-400"
                                                                style={{ width: `${r.total ? (r.manual / r.total) * r.pct_of_total : 0}%` }} />
                                                        </div>
                                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums w-9 text-right">
                                                            {r.pct_of_total}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            );
                        })}
                        {!byStage.length && !loading && (
                            <tbody>
                                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No leads in range.</td></tr>
                            </tbody>
                        )}
                    </table>
                </div>
            </div>

            {/* ── the leads themselves ── */}
            <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 px-1 flex-shrink-0">
                Leads
                <span className="ml-1.5 font-normal text-[10px] text-gray-400 tabular-nums">
                    {num(totalCount)} matching{stages.length ? ` · ${stages.join(", ")}` : ""}
                </span>
            </h2>
            {/* ── detail table ──
                 flex-shrink-0, like every other direct child of ContainerCard.
                 ContainerCard is a FIXED h-[calc(100vh-64px)] flex-col with
                 overflow-y-auto: for it to scroll rather than squash, nothing
                 inside may shrink. This wrapper carries overflow-x-auto, which
                 makes it a scroll container, so its min-height:auto resolves to
                 0 — leave it shrinkable and it absorbs the entire overflow and
                 collapses to a bare border, with no rows between the heading
                 and the pager. */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 flex-shrink-0">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr className="text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2.5 text-left font-medium">#</th>
                            <th className="px-3 py-2.5 text-left font-medium">Created</th>
                            <th className="px-3 py-2.5 text-left font-medium">Lead</th>
                            <th className="px-3 py-2.5 text-left font-medium">Contact</th>
                            <th className="px-3 py-2.5 text-left font-medium">Origin</th>
                            <th className="px-3 py-2.5 text-left font-medium">Stage</th>
                            <th className="px-3 py-2.5 text-left font-medium">Ad / Form</th>
                            <th className="px-3 py-2.5 text-left font-medium">Assigned to</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {rows.map((row, idx) => (
                            <tr key={row.id}
                                onClick={() => navigate("/customers/customerdetails", { state: { formEdit: true, row: { id: row.id } } })}
                                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">
                                    {(page - 1) * pageSize + idx + 1}
                                </td>
                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                    <div>{formatDate(row.created_date)}</div>
                                    <div className="text-[10px] text-gray-400 dark:text-gray-500">
                                        {formatTime(row.created_date)}
                                    </div>
                                </td>
                                <td className="px-3 py-2.5 text-gray-800 dark:text-gray-200 max-w-[180px]">
                                    <TruncatedText text={row.customer_name || `#${row.id}`} />
                                    {row.city && <p className="text-[10px] text-gray-400">{row.city}</p>}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                    {row.contact_number || "—"}
                                </td>
                                <td className="px-3 py-2.5">
                                    <OriginPill origin={row.origin}
                                        hint={row.lead_source ? `Recorded source: ${row.lead_source}` : undefined} />
                                </td>
                                <td className="px-3 py-2.5">
                                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium",
                                        getStageBadgeClass(row.lead_stage))}>
                                        {row.lead_stage || "—"}
                                    </span>
                                </td>
                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[160px]">
                                    {row.ad_name || row.lead_form_name
                                        ? <TruncatedText text={row.ad_name || row.lead_form_name} />
                                        : "—"}
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                    {row.assigned_to_name || "Unassigned"}
                                </td>
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                                {loading ? "Loading…" : "No leads match these filters."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── paging (server-side: the table can be tens of thousands of rows) ── */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-1 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                        {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n} / page</option>)}
                    </select>
                    <span className="text-[10px] text-gray-400">
                        {totalCount === 0 ? "0" :
                            `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)}`} of {num(totalCount)}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setPage(1)} disabled={page === 1}
                        className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                        <ChevronLeft className="h-3 w-3" />
                    </button>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 px-1.5 tabular-nums">
                        {page} / {totalPages}
                    </span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                        className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                        <ChevronRight className="h-3 w-3" />
                    </button>
                    <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                        className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                </div>
            </div>
        </ContainerCard>
    );
}
